import {
  VoiceFormDefinition,
  VoiceFormIntentId,
  getVoiceFormById,
} from "./voiceFormRegistry";
import { isInternalTranscriptLeak, VOICE_MENU_GREETING } from "./voiceFormPrompts";

export interface DetectedVoiceIntent {
  formId: VoiceFormIntentId;
  definition: VoiceFormDefinition;
  matchedText: string;
}

/** Prefer add driver/vehicle before file a claim (menu text mentions all three). */
const INTENT_MATCH_ORDER: VoiceFormIntentId[] = [
  "add_driver",
  "add_vehicle",
  "file_claim",
];

export function detectVoiceFormIntent(text: string): DetectedVoiceIntent | null {
  const normalized = text.trim();
  if (!normalized) return null;

  for (const formId of INTENT_MATCH_ORDER) {
    const definition = getVoiceFormById(formId);
    if (!definition) continue;
    for (const pattern of definition.intentPatterns) {
      const match = normalized.match(pattern);
      if (match) {
        return {
          formId: definition.id,
          definition,
          matchedText: match[0],
        };
      }
    }
  }
  return null;
}

export function isMenuGreetingTurn(text: string): boolean {
  const t = text.trim();
  if (!t) return false;
  if (t === VOICE_MENU_GREETING) return true;
  return /^how can i help you today\??$/i.test(t);
}

/** Caller listed multiple menu options (agent greeting or echo). */
export function isMenuCatalogUtterance(text: string): boolean {
  if (isMenuGreetingTurn(text) || isInternalTranscriptLeak(text)) return true;
  const lower = text.toLowerCase();
  let menuOptions = 0;
  if (/\bfile\s+a\s+claim\b/.test(lower)) menuOptions += 1;
  if (/\badd\s+(?:a\s+)?driver\b/.test(lower)) menuOptions += 1;
  if (/\badd\s+(?:a\s+)?(?:vehicle|car)\b/.test(lower)) menuOptions += 1;
  return menuOptions >= 2;
}

/** Mic/speaker picked up the agent reading the menu options. */
export function isLikelyEchoOfAgentMenu(
  claimantText: string,
  turns: { speaker: string; text: string; streaming?: boolean }[]
): boolean {
  const fragment = claimantText.trim().toLowerCase();
  if (!fragment || fragment.length > 80) return false;

  const agentFinals = turns.filter(
    (t) =>
      t.speaker === "Agent" &&
      !t.streaming &&
      (t.text?.trim().length ?? 0) > 0
  );
  const lastAgent = agentFinals[agentFinals.length - 1];
  if (!lastAgent?.text) return false;

  const agentLower = lastAgent.text.toLowerCase();
  if (!/how can i help you today/i.test(agentLower)) return false;
  return agentLower.includes(fragment);
}

/** Match intent from one claimant utterance (not agent/menu echo). */
export function detectVoiceFormIntentFromClaimantUtterance(
  text: string,
  turns: { speaker: string; text: string; streaming?: boolean }[] = []
): DetectedVoiceIntent | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  if (isMenuCatalogUtterance(trimmed) || isLikelyEchoOfAgentMenu(trimmed, turns)) {
    return null;
  }
  return (
    detectVoiceFormIntent(trimmed) ?? detectClaimFromIncidentDescription(trimmed)
  );
}

/** Auto-select claim intake when the caller describes a loss (no "file a claim" required). */
export function detectClaimFromIncidentDescription(
  text: string
): DetectedVoiceIntent | null {
  const trimmed = text.trim();
  if (!trimmed || isMenuCatalogUtterance(trimmed)) return null;

  const incidentPatterns = [
    /\b(car|auto|vehicle)\s+accident\b/i,
    /\b(i\s+was\s+in\s+an?\s+accident|had\s+an?\s+accident)\b/i,
    /\b(hit\s+me|rear[- ]?ended|someone\s+hit)\b/i,
    /\b(collision|crash)\b/i,
    /\b(water\s+damage|pipe\s+burst)\b/i,
    /\b(fire|kitchen\s+fire|house\s+fire)\b/i,
    /\b(stolen|theft|burglar|break[- ]?in)\b/i,
    /\b(hail\s+damage|flood(?:ing)?)\b/i,
    /\b(windshield|glass\s+damage)\b/i,
    /\b(single\s+vehicle|ran\s+off\s+the\s+road)\b/i,
    /\b(vandal)/i,
  ];

  for (const pattern of incidentPatterns) {
    if (pattern.test(trimmed)) {
      const definition = getVoiceFormById("file_claim");
      if (definition) {
        return { formId: "file_claim", definition, matchedText: trimmed };
      }
    }
  }
  return null;
}

/**
 * Resolve intent from claimant speech only (never from agent lines).
 * Uses the most recent claimant turn (finalized when available).
 *
 * When {@code currentFormId} is set, only returns a *different* form so mid-call
 * switches (e.g. file claim → add vehicle) work without re-locking the old intent.
 */
export function detectVoiceFormIntentFromTranscript(
  turns: { speaker: string; text: string; streaming?: boolean }[],
  currentFormId: VoiceFormIntentId | null = null
): DetectedVoiceIntent | null {
  const claimantTurns = turns.filter(
    (t) => t.speaker === "Claimant" && t.text?.trim()
  );
  if (!claimantTurns.length) return null;

  const finalized = claimantTurns.filter((t) => !t.streaming);
  const pool = finalized.length ? finalized : claimantTurns;

  for (let i = pool.length - 1; i >= 0; i -= 1) {
    const hit = detectVoiceFormIntentFromClaimantUtterance(pool[i].text!, turns);
    if (!hit) continue;
    if (currentFormId === null) return hit;
    if (hit.formId !== currentFormId) return hit;
    // Newest intent phrase is for the active form — do not scan older turns.
    return null;
  }

  return null;
}
